import React, { useEffect, useMemo, useRef, useState } from "react";
import { hostelAdministratorsApi } from "../../config/api";
import { apiClient, apiFormDataClient } from "../../utils/apiClient";
import "./HostelAdministrators.scss";

const roleTypes = ["Warden", "Superintendent", "Assistant Warden", "Deputy Warden", "Hostel Coordinator", "Caretaker"];
const genderCategories = ["Boys", "Girls", "Co-ed"];
const hostels = [
  "Boys Hostel Block A", "Boys Hostel Block B", "Boys Hostel Block C",
  "Girls Hostel Block A", "Girls Hostel Block B", "Co-ed Hostel",
];

const defaultForm = {
  _id: null, Profile_Photo: "", Full_Name: "", Designation: "", Role_Type: "", Short_Description: "",
  Email: "", Phone: "", Alternate_Phone: "", Assigned_Hostel: "", Gender_Category: "",
  Office_Address: "", Office_Timings: "", Display_Order: "", Status: "Active",
};

const HostelAdministrators = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [searchHostel, setSearchHostel] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [dropdownOpenId, setDropdownOpenId] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [modalType, setModalType] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState(defaultForm);
  const [confirmId, setConfirmId] = useState(null);
  const [confirmAction, setConfirmAction] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => { fetchAdministrators(); }, []);

  const fetchAdministrators = async () => {
    try {
      setLoading(true);
      const response = await apiClient(hostelAdministratorsApi);
      const result = await response.json();
      if (response.ok) { setData(result.data || []); }
    } catch (error) {
      console.error("Error fetching hostel administrators:", error);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    return data.filter((item) => {
      const matchName = (item.Full_Name || "").toLowerCase().includes(searchName.toLowerCase());
      const matchHostel = (item.Assigned_Hostel || "").toLowerCase().includes(searchHostel.toLowerCase());
      const matchStatus = filterStatus === "all" || item.Status === filterStatus;
      return matchName && matchHostel && matchStatus;
    });
  }, [data, searchName, searchHostel, filterStatus]);

  useEffect(() => {
    const handleOutside = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpenId(null); };
    const closeDropdown = () => setDropdownOpenId(null);
    document.addEventListener("mousedown", handleOutside);
    window.addEventListener("scroll", closeDropdown, true);
    window.addEventListener("resize", closeDropdown);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      window.removeEventListener("scroll", closeDropdown, true);
      window.removeEventListener("resize", closeDropdown);
    };
  }, []);

  const handleDotsClick = (e, id) => {
    e.stopPropagation();
    if (dropdownOpenId === id) { setDropdownOpenId(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + 8, left: rect.right - 180 });
    setDropdownOpenId(id);
  };

  const openAdd = () => { setFormData(defaultForm); setSelectedFile(null); setPreviewUrl(null); setModalType("add"); setDropdownOpenId(null); };
  const openView = (item) => { setSelectedItem(item); setModalType("view"); setDropdownOpenId(null); };
  const openEdit = (item) => { setFormData({ ...item }); setSelectedFile(null); setPreviewUrl(null); setModalType("edit"); setDropdownOpenId(null); };
  const closeModal = () => { setModalType(""); setSelectedItem(null); setFormData(defaultForm); setSelectedFile(null); setPreviewUrl(null); };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const removePhoto = () => {
    setFormData(p => ({ ...p, Profile_Photo: "" }));
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const method = modalType === "add" ? "POST" : "PUT";
      const url = modalType === "add" ? hostelAdministratorsApi : `${hostelAdministratorsApi}/${formData._id}`;
      
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (key !== 'Profile_Photo' && formData[key] !== null) {
          formDataToSend.append(key, formData[key]);
        }
      });

      if (selectedFile) {
        formDataToSend.append('Profile_Photo', selectedFile);
      } else if (formData.Profile_Photo) {
        formDataToSend.append('Profile_Photo', formData.Profile_Photo);
      }

      const response = await apiFormDataClient(url, { method, body: formDataToSend });
      if (response.ok) { fetchAdministrators(); closeModal(); }
    } catch (error) {
      console.error("Error saving administrator:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      setLoading(true);
      const item = data.find(m => m._id === id);
      const newStatus = item.Status === "Active" ? "Inactive" : "Active";
      await apiClient(`${hostelAdministratorsApi}/${id}`, { method: "PUT", body: JSON.stringify({ ...item, Status: newStatus }) });
      fetchAdministrators(); setConfirmId(null);
    } catch (error) {
      console.error("Error toggling status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await apiClient(`${hostelAdministratorsApi}/${id}`, { method: "DELETE" });
      fetchAdministrators(); setConfirmId(null);
    } catch (error) {
      console.error("Error deleting administrator:", error);
    } finally {
      setLoading(false);
    }
  };

  const openConfirm = (id, action) => { setDropdownOpenId(null); setConfirmId(id); setConfirmAction(action); };

  return (
    <div className="ha-page">
      <div className="ha-page__header">
        <div><h1>Hostel Administrators</h1><p>Manage hostel administrators, wardens, and superintendents records.</p></div>
        <button className="ha-add-btn" type="button" onClick={openAdd}><span>+</span> Add Administrator</button>
      </div>

      <div className="ha-filter-card">
        <div className="ha-filter-grid">
          <div className="form-group"><label>Search by Name</label><input type="text" placeholder="Enter name" value={searchName} onChange={(e) => setSearchName(e.target.value)} /></div>
          <div className="form-group"><label>Search by Hostel</label><input type="text" placeholder="Enter hostel" value={searchHostel} onChange={(e) => setSearchHostel(e.target.value)} /></div>
          <div className="form-group"><label>Filter by Status</label><select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}><option value="all">All Status</option><option value="active">Active</option><option value="inactive">Inactive</option></select></div>
        </div>
      </div>

      {loading && <div className="loading-overlay">Loading...</div>}

      <div className="ha-table-card">
        <div className="table-wrap">
          <table>
            <thead><tr><th>Photo</th><th>Name</th><th>Designation</th><th>Assigned Hostel</th><th>Status</th><th className="actions-col">Actions</th></tr></thead>
            <tbody>
              {filtered.length > 0 ? (
                filtered.map((item) => (
                  <tr key={item._id}>
                    <td><div className="table-photo">{item.Profile_Photo ? <img src={item.Profile_Photo} alt="" /> : <div className="photo-placeholder">{(item.Full_Name || "?").charAt(0)}</div>}</div></td>
                    <td><div className="name-cell"><strong>{item.Full_Name}</strong><span>{item.Role_Type}</span></div></td>
                    <td>{item.Designation}</td><td>{item.Assigned_Hostel}</td>
                    <td><span className={`status-badge ${item.Status}`}>{item.Status === "Active" ? "Active" : "Inactive"}</span></td>
                    <td className="actions-cell"><button className="dots-btn" type="button" onClick={(e) => handleDotsClick(e, item._id)}>⋮</button></td>
                  </tr>
                ))
              ) : (<tr><td colSpan="6" className="empty-cell">No records found.</td></tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {dropdownOpenId !== null && (
        <div ref={dropdownRef} className="action-dropdown" style={{ position: "fixed", top: dropdownPos.top, left: dropdownPos.left, zIndex: 99999 }}>
          <button type="button" onClick={() => openView(data.find(i => i._id === dropdownOpenId))}><svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" /><circle cx="12" cy="12" r="3" /></svg> View</button>
          <button type="button" onClick={() => openEdit(data.find(i => i._id === dropdownOpenId))}><svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" /></svg> Edit</button>
          {data.find(i => i._id === dropdownOpenId)?.Status === "Active" ? (
            <button type="button" className="deactivate" onClick={() => openConfirm(dropdownOpenId, "deactivate")}><svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="8" y1="12" x2="16" y2="12" /></svg> Deactivate</button>
          ) : (
            <button type="button" className="activate" onClick={() => openConfirm(dropdownOpenId, "activate")}><svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="16" /><line x1="8" y1="12" x2="16" y2="12" /></svg> Activate</button>
          )}
          <button type="button" className="delete" onClick={() => openConfirm(dropdownOpenId, "delete")}><svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4h6v2" /></svg> Delete</button>
        </div>
      )}

      {confirmId !== null && (
        <div className="modal-overlay" onClick={() => setConfirmId(null)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-icon">{confirmAction === "delete" ? <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="#ef4444" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4h6v2" /></svg> : <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="#f59e0b" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>}</div>
            <h3>{confirmAction === "delete" ? "Delete Record?" : confirmAction === "deactivate" ? "Deactivate Record?" : "Activate Record?"}</h3>
            <p>{confirmAction === "delete" ? "This will permanently remove the record from the database." : confirmAction === "deactivate" ? "This record will be hidden from the public website." : "This record will be made visible on the public website."}</p>
            <div className="confirm-actions"><button type="button" className="btn btn-light" onClick={() => setConfirmId(null)}>Cancel</button><button type="button" className={`btn ${confirmAction === "delete" ? "btn-danger" : "btn-warning"}`} onClick={() => confirmAction === "delete" ? handleDelete(confirmId) : handleToggleStatus(confirmId)}>Confirm</button></div>
          </div>
        </div>
      )}

      {(modalType === "add" || modalType === "edit") && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="ha-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2>{modalType === "add" ? "Add Administrator" : "Edit Administrator"}</h2><button className="modal-close" type="button" onClick={closeModal}>×</button></div>
            <form className="modal-form" onSubmit={handleSave}>
              <div className="form-section-label">Media</div>
              <div className="form-group">
                <label>Profile Photo</label>
                <div className="photo-upload-box">
                  {(previewUrl || formData.Profile_Photo) ? (
                    <div className="photo-preview-wrapper">
                      <img src={previewUrl || formData.Profile_Photo} alt="Preview" className="photo-preview" />
                      <div className="photo-actions">
                        <label className="photo-change-btn">Change Photo<input type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} /></label>
                        <button type="button" className="photo-remove-btn" onClick={removePhoto}>Remove Photo</button>
                      </div>
                    </div>
                  ) : (
                    <div className="photo-upload-placeholder">
                      <label className="file-upload-label">
                        <div className="upload-icon">📷</div>
                        <span>Click to upload photo</span>
                        <input type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} />
                      </label>
                      <small>JPG, PNG, WEBP supported</small>
                    </div>
                  )}
                </div>
              </div>
              <div className="form-section-label">Basic Info</div>
              <div className="form-row">
                <div className="form-group"><label>Full Name *</label><input type="text" value={formData.Full_Name || ""} onChange={e => setFormData({...formData, Full_Name: e.target.value})} required /></div>
                <div className="form-group"><label>Designation *</label><input type="text" value={formData.Designation || ""} onChange={e => setFormData({...formData, Designation: e.target.value})} required /></div>
              </div>
              <div className="form-row">
                 <div className="form-group"><label>Role Type</label><select value={formData.Role_Type || ""} onChange={e => setFormData({...formData, Role_Type: e.target.value})} required><option value="">Select role type</option>{roleTypes.map(r => <option key={r} value={r}>{r}</option>)}</select></div>
                <div className="form-group"><label>Assigned Hostel</label><select value={formData.Assigned_Hostel || ""} onChange={e => setFormData({...formData, Assigned_Hostel: e.target.value})}><option value="">Select hostel</option>{hostels.map(h => <option key={h} value={h}>{h}</option>)}</select></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Gender Category</label><select value={formData.Gender_Category || ""} onChange={e => setFormData({...formData, Gender_Category: e.target.value})}><option value="">Select category</option>{genderCategories.map(g => <option key={g} value={g}>{g}</option>)}</select></div>
                <div className="form-group"><label>Display Order</label><input type="number" value={formData.Display_Order || ""} onChange={e => setFormData({...formData, Display_Order: e.target.value})} /></div>
              </div>
              <div className="form-group"><label>Short Description</label><textarea value={formData.Short_Description || ""} onChange={e => setFormData({...formData, Short_Description: e.target.value})} rows="2" /></div>
              <div className="form-section-label">Contact Info</div>
              <div className="form-row">
                <div className="form-group"><label>Email *</label><input type="email" value={formData.Email || ""} onChange={e => setFormData({...formData, Email: e.target.value})} required /></div>
                <div className="form-group"><label>Phone *</label><input type="text" value={formData.Phone || ""} onChange={e => setFormData({...formData, Phone: e.target.value})} required /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Alternate Phone</label><input type="text" value={formData.Alternate_Phone || ""} onChange={e => setFormData({...formData, Alternate_Phone: e.target.value})} placeholder="e.g. +91..." /></div>
                <div className="form-group"><label>Office Timings</label><input type="text" value={formData.Office_Timings || ""} onChange={e => setFormData({...formData, Office_Timings: e.target.value})} placeholder="e.g. 24/7 or 9AM-5PM" /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Office Address</label><input type="text" value={formData.Office_Address || ""} onChange={e => setFormData({...formData, Office_Address: e.target.value})} placeholder="e.g. Room 1, Block A" /></div>
              </div>
              <div className="modal-actions"><button type="button" className="btn btn-light" onClick={closeModal}>Cancel</button><button type="submit" className="btn btn-primary">Save Admin</button></div>
            </form>
          </div>
        </div>
      )}

      {modalType === "view" && selectedItem && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="ha-modal view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2>View Administrator Details</h2><button className="modal-close" type="button" onClick={closeModal}>×</button></div>
            <div className="view-body">
              <div className="view-profile-top">{selectedItem.Profile_Photo ? <img src={selectedItem.Profile_Photo} alt="" className="view-photo" /> : <div className="view-photo-placeholder">{(selectedItem.Full_Name || "?").charAt(0)}</div>}<div className="view-profile-info"><h3>{selectedItem.Full_Name}</h3><span className="view-designation">{selectedItem.Designation}</span></div></div>
              <div className="view-section-label">Contact & Assignment Info</div>
              <div className="view-grid">
                <div className="view-item"><span>Email</span><strong>{selectedItem.Email || "-"}</strong></div>
                <div className="view-item"><span>Phone</span><strong>{selectedItem.Phone || "-"}</strong></div>
                <div className="view-item"><span>Alt Phone</span><strong>{selectedItem.Alternate_Phone || "-"}</strong></div>
                <div className="view-item"><span>Hostel</span><strong>{selectedItem.Assigned_Hostel || "-"}</strong></div>
                <div className="view-item"><span>Office Timings</span><strong>{selectedItem.Office_Timings || "-"}</strong></div>
                <div className="view-item"><span>Office Address</span><strong>{selectedItem.Office_Address || "-"}</strong></div>
                <div className="view-item"><span>Display Order</span><strong>{selectedItem.Display_Order || "-"}</strong></div>
              </div>
              <div className="view-footer-actions"><button type="button" className="btn btn-light" onClick={closeModal}>Close</button><button type="button" className="btn btn-primary" onClick={() => { setFormData({...selectedItem}); setModalType("edit"); }}>Edit Administrator</button></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HostelAdministrators;